import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification, ApiNotification } from 'app/layout/common/notifications/notifications.types';
import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { environment } from '@env/environment';

export interface NotificationListResult {
    items: Notification[];
    total: number;
    page: number;
    perPage: number;
    view: 'open' | 'all';
}

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    private _debug(label: string, payload?: any, color: string = '#7c3aed'): void
    {
        try {
            // eslint-disable-next-line no-console
            console.log(`%c[NotificationsService] ${label}`, `color:${color};font-weight:600`, payload ?? '');
        } catch {
            // no-op
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Getter for unread count
     */
    get unreadCount$(): Observable<number>
    {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications from the real API
     */
    getAll(options?: { view?: 'open' | 'all'; page?: number; perPage?: number; companyId?: number | null }): Observable<NotificationListResult>
    {
        const view = options?.view ?? 'open';
        const page = options?.page ?? 1;
        const perPage = options?.perPage ?? 20;
        const companyId = options?.companyId ?? null;
        let url = `${environment.serverUrl}/v1/notifications?view=${view}&page=${page}&per_page=${perPage}`;
        if (companyId && companyId > 0) {
            url += `&company_id=${companyId}`;
        }
        this._debug('getAll() request', { view, page, perPage, companyId, url }, '#2563eb');

        return this._httpClient.get<any>(url).pipe(
            map((response) => {
                this._debug('getAll() raw response', response, '#1d4ed8');
                if (response?.status === 'OK' && Array.isArray(response.data)) {
                    const items = response.data.map((apiNotif: ApiNotification) => this._mapApiToNotification(apiNotif));
                    this._debug('getAll() mapped result', {
                        view,
                        page: Number(response?.page ?? page),
                        perPage: Number(response?.per_page ?? perPage),
                        total: Number(response?.total ?? items.length),
                        items: items.length,
                    }, '#0f766e');
                    return {
                        items,
                        total: Number(response?.total ?? items.length),
                        page: Number(response?.page ?? page),
                        perPage: Number(response?.per_page ?? perPage),
                        view,
                    } as NotificationListResult;
                }
                return {
                    items: [],
                    total: 0,
                    page,
                    perPage,
                    view,
                } as NotificationListResult;
            }),
            tap((result) => {
                this._notifications.next(result.items);
                this._debug('notifications$ next()', {
                    view: result.view,
                    page: result.page,
                    total: result.total,
                    emittedItems: result.items.length,
                }, '#0f766e');
            }),
            catchError((error) => {
                this._debug('getAll() failed', error, '#dc2626');
                this._notifications.next([]);
                return of({
                    items: [],
                    total: 0,
                    page,
                    perPage,
                    view,
                } as NotificationListResult);
            }),
        );
    }

    /**
     * Get unread notification count from API
     */
    getUnreadCount(): Observable<number>
    {
        const url = `${environment.serverUrl}/v1/messages/counters`;
        this._debug('getUnreadCount() request', { url }, '#a21caf');

        return this._httpClient.get<any>(url).pipe(
            map((response) => {
                const count = Number(response?.data?.totals?.notifications_needs_action || 0);
                this._unreadCount.next(count);
                this._debug('getUnreadCount() response', {
                    notifications_needs_action: count,
                    totals: response?.data?.totals || {},
                }, '#7e22ce');
                return count;
            }),
            catchError((error) => {
                this._debug('getUnreadCount() failed', error, '#dc2626');
                return of(0);
            }),
        );
    }

    /**
     * Create a notification (not used with real API - notifications are created server-side)
     */
    create(notification: Notification): Observable<Notification>
    {
        // With the real API, notifications are created server-side.
        // This is kept for backwards compatibility but is essentially a no-op.
        return of(notification);
    }

    /**
     * Update the notification (toggle read status)
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        this._debug('update() start', {
            id,
            read: notification?.read,
            attention_state: notification?.attention_state,
        }, '#ea580c');
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => {
                // Update locally
                const index = notifications.findIndex(item => item.id === id);
                if (index >= 0) {
                    notifications[index] = notification;
                    this._notifications.next(notifications);
                    this._unreadCount.next(notifications.filter(n => !n.read).length);
                }

                // If marking as read, call the API.
                // Include student_course_step_id so the backend can resolve
                // all duplicate notifications for the same step at once.
                if (notification.read) {
                    const url = `${environment.serverUrl}/v1/notifications/mark-read`;
                    const payload: any = { ids: [parseInt(id, 10)] };
                    if (notification.student_course_step_id) {
                        payload.step_ids = [notification.student_course_step_id];
                    }
                    this._debug('update() mark-read request', { id, url, payload }, '#c2410c');
                    return this._httpClient.post<any>(url, payload).pipe(
                        map((res) => {
                            const directUpdated = Number(res?.data?.updated ?? 0);
                            const siblingUpdated = Number(res?.data?.sibling_updated ?? 0);
                            // Number of DISTINCT notifications resolved on the server.
                            // sibling_updated already includes the directly-updated row when
                            // the sibling query runs, so use the larger of the two to avoid
                            // double-counting.
                            const totalResolved = Math.max(directUpdated, siblingUpdated);
                            this._debug('update() mark-read response', {
                                id,
                                updated: directUpdated,
                                sibling_updated: siblingUpdated,
                                totalResolved,
                                status: res?.status,
                            }, '#9a3412');
                            // Optimistically reduce the global unread counter by the number
                            // the server confirmed were resolved.  This keeps the badge in
                            // sync immediately without waiting for a full counter reload.
                            if (totalResolved > 0) {
                                const current = this._unreadCount.getValue();
                                const next = Math.max(0, current - totalResolved);
                                this._unreadCount.next(next);
                                this._debug('update() unread count optimistic update', { from: current, to: next, resolved: totalResolved }, '#047857');
                            } else {
                                // Fallback: decrement by 1 for the item we just marked.
                                const current = this._unreadCount.getValue();
                                this._unreadCount.next(Math.max(0, current - 1));
                            }
                            return notification;
                        }),
                        catchError((err) => {
                            this._debug('update() mark-read failed', { id, err }, '#dc2626');
                            return of(notification);
                        }),
                    );
                }

                return of(notification);
            }),
        );
    }

    /**
     * Delete the given notification (mark as read and hide locally)
     */
    delete(id: string): Observable<boolean>
    {
        this._debug('delete() start', { id }, '#be123c');
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => {
                // Remove locally
                const index = notifications.findIndex(item => item.id === id);
                if (index >= 0) {
                    notifications.splice(index, 1);
                    this._notifications.next(notifications);
                    this._unreadCount.next(notifications.filter(n => !n.read).length);
                }

                // Mark as read on server
                const url = `${environment.serverUrl}/v1/notifications/mark-read`;
                return this._httpClient.post<any>(url, { ids: [parseInt(id, 10)] }).pipe(
                    map((res) => {
                        this._debug('delete() mark-read response', { id, res }, '#9f1239');
                        return true;
                    }),
                    catchError((err) => {
                        this._debug('delete() mark-read failed', { id, err }, '#dc2626');
                        return of(true);
                    }),
                );
            }),
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        const url = `${environment.serverUrl}/v1/notifications/mark-read`;
        this._debug('markAllAsRead() request', { url }, '#065f46');

        return this._httpClient.post<any>(url, { all: true }).pipe(
            tap(() => {
                // Update local state
                this.notifications$.pipe(take(1)).subscribe(notifications => {
                    notifications.forEach(n => n.read = true);
                    this._notifications.next([...notifications]);
                    this._unreadCount.next(0);
                    this._debug('markAllAsRead() local state updated', {
                        remainingLocalUnread: notifications.filter(n => !n.read).length,
                        totalLocal: notifications.length,
                    }, '#047857');
                });
            }),
            map(() => true),
            catchError((error) => {
                this._debug('markAllAsRead() failed', error, '#dc2626');
                return of(false);
            }),
        );
    }

    /**
     * Mark all notifications related to a specific student course step as done/read.
     * Called automatically when a supervisor approves or rejects a step.
     */
    markDoneByStep(studentCourseStepId: number): Observable<boolean>
    {
        this._debug('markDoneByStep() start', { studentCourseStepId }, '#0369a1');
        if (!studentCourseStepId || studentCourseStepId <= 0) {
            return of(false);
        }

        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => {
                // Find unread notifications matching this step
                const matching = notifications.filter(
                    n => !n.read && n.student_course_step_id === studentCourseStepId
                );

                if (matching.length === 0) {
                    return of(true); // Nothing to mark
                }

                // Mark locally
                matching.forEach(n => n.read = true);
                this._notifications.next([...notifications]);
                this._unreadCount.next(notifications.filter(n => !n.read).length);

                // Mark on server
                const ids = matching.map(n => parseInt(n.id, 10)).filter(id => !isNaN(id));
                if (ids.length > 0) {
                    const url = `${environment.serverUrl}/v1/notifications/mark-read`;
                    this._debug('markDoneByStep() request', { studentCourseStepId, ids, url }, '#0284c7');
                    return this._httpClient.post<any>(url, { ids }).pipe(
                        map((res) => {
                            this._debug('markDoneByStep() response', { studentCourseStepId, updatedIds: ids.length, res }, '#0e7490');
                            return true;
                        }),
                        catchError((err) => {
                            this._debug('markDoneByStep() failed', { studentCourseStepId, err }, '#dc2626');
                            return of(true);
                        }),
                    );
                }
                return of(true);
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Human-readable action type labels
     */
    private static readonly ACTION_TYPE_LABELS: { [key: number]: string } = {
        1: 'Video',
        2: 'Read Text',
        3: 'PDF',
        4: 'Essay',
        5: 'Drill',
        6: 'Checkout',
        7: 'Course Start',
        8: 'Completion',
        9: 'Video Drill',
        10: 'Clear Word',
        11: 'Policy',
        12: 'YouTube',
        13: 'Quiz',
        14: 'Final Test',
        15: 'How Are You',
        16: 'Twinning',
        17: 'Supervisor Drill',
        18: 'Gallery',
    };

    /**
     * Map API notification data to the Notification interface
     */
    private _mapApiToNotification(apiNotif: ApiNotification): Notification
    {
        let icon = 'heroicons_outline:bell';
        let link: string | undefined;
        let useRouter = false;

        // Set icon based on message_type
        switch (apiNotif.message_type) {
            case 'student_submission':
                icon = 'heroicons_outline:document-text';
                break;
            case 'step_approval':
                icon = 'heroicons_outline:check-circle';
                break;
            case 'step_rejection':
                icon = 'heroicons_outline:x-circle';
                break;
            case 'system_notification':
                icon = 'heroicons_outline:bell-alert';
                break;
            // LMS notification types
            case 'badge_earned':
                icon = 'heroicons_outline:star';
                break;
            case 'course_completed':
                icon = 'heroicons_outline:trophy';
                break;
            case 'nachschulung_triggered':
                icon = 'heroicons_outline:exclamation-triangle';
                break;
            case 'nachschulung_completed':
                icon = 'heroicons_outline:check-badge';
                break;
            case 'study_plan_assigned':
                icon = 'heroicons_outline:academic-cap';
                break;
            case 'study_plan_completed':
                icon = 'heroicons_outline:academic-cap';
                break;
            case 'certificate_issued':
                icon = 'heroicons_outline:document-text';
                break;
            case 'prerequisite_met':
                icon = 'heroicons_outline:lock-open';
                break;
            case 'recurring_course_due':
                icon = 'heroicons_outline:clock';
                break;
            case 'rating_updated':
                icon = 'heroicons_outline:chart-bar';
                break;
        }

        // ── Build descriptive title ──────────────────────
        // Show student name as the primary title (most important for supervisors)
        let title = apiNotif.student_name || 'Student';

        // ── Build rich description ───────────────────────
        // Format: "Course Name — Step #N: Step Title (Type)"
        const descParts: string[] = [];

        if (apiNotif.course_title) {
            descParts.push(apiNotif.course_title);
        }

        // Step number + title
        const stepNum = (apiNotif as any).step_sort;
        if (stepNum && apiNotif.step_title) {
            descParts.push(`Step #${stepNum}: ${apiNotif.step_title}`);
        } else if (stepNum) {
            descParts.push(`Step #${stepNum}`);
        } else if (apiNotif.step_title) {
            descParts.push(apiNotif.step_title);
        }

        // Action type label
        if (apiNotif.actiontype_id) {
            const typeLabel = NotificationsService.ACTION_TYPE_LABELS[apiNotif.actiontype_id];
            if (typeLabel) {
                descParts.push(typeLabel);
            }
        }

        let description = descParts.join(' — ');

        // Fallback to message content if nothing else
        if (!description && apiNotif.message) {
            const plainText = apiNotif.message.replace(/<[^>]*>/g, '').trim();
            description = plainText.length > 120 ? plainText.substring(0, 120) + '...' : plainText;
        }

        // Build link based on notification type
        switch (apiNotif.message_type) {
            case 'badge_earned':
                if (apiNotif.badge_id) {
                    link = `/badges-mgmt/${apiNotif.badge_id}`;
                    useRouter = true;
                }
                break;
            case 'study_plan_assigned':
            case 'study_plan_completed':
                if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            case 'nachschulung_triggered':
            case 'nachschulung_completed':
                if (apiNotif.student_id && apiNotif.student_course_id) {
                    link = `/supervisor/${apiNotif.student_id}/${apiNotif.student_course_id}`;
                    useRouter = true;
                }
                break;
            case 'certificate_issued':
                link = `/certificates`;
                useRouter = true;
                break;
            case 'recurring_course_due':
                if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            default:
                if (apiNotif.student_id && apiNotif.student_course_id) {
                    link = `/supervisor/${apiNotif.student_id}/${apiNotif.student_course_id}`;
                    useRouter = true;
                }
                break;
        }

        return {
            id: String(apiNotif.t_ot_messagesid),
            icon,
            title,
            description,
            time: apiNotif.created,
            link,
            useRouter,
            read: apiNotif.status_id !== 1, // 1 = UNREAD
            message_type: apiNotif.message_type,
            attention_state: apiNotif.attention_state,
            ack_required: apiNotif.ack_required,
            ack_at: apiNotif.ack_at,
            delivered_at: apiNotif.delivered_at,
            read_at: apiNotif.read_at,
            student_name: apiNotif.student_name,
            company_name: apiNotif.company_name,
            course_title: apiNotif.course_title,
            step_title: apiNotif.step_title,
            student_id: apiNotif.student_id,
            student_course_id: apiNotif.student_course_id,
            student_course_step_id: apiNotif.student_course_step_id,
            step_id: apiNotif.step_id,
            step_sort: (apiNotif as any).step_sort,
            actiontype_id: apiNotif.actiontype_id,
            company_id: apiNotif.company_id,
            badge_id: apiNotif.badge_id,
            badge_title: apiNotif.badge_title,
            rating_tier: apiNotif.rating_tier,
            certificate_id: apiNotif.certificate_id,
            study_plan_id: apiNotif.study_plan_id,
            group_id: apiNotif.group_id,
        };
    }
}
